import { Link, useNavigate } from 'react-router-dom';
import { logoutUser } from '../../services/session';
import { ATENDABOT_LOGO } from '../../branding/assets';
import './AtendabotLanding.css';

const FEATURES = [
  {
    icon: 'spark',
    title: 'Atendimento com IA',
    description: 'Automatize respostas recorrentes com mais agilidade, clareza e consistência.',
  },
  {
    icon: 'inbox',
    title: 'Conversas centralizadas',
    description: 'Reúna os atendimentos em um ambiente organizado e fácil de acompanhar.',
  },
  {
    icon: 'history',
    title: 'Histórico e contexto',
    description: 'Consulte interações anteriores e continue cada conversa com mais contexto.',
  },
  {
    icon: 'chart',
    title: 'Painel e indicadores',
    description: 'Visualize a rotina de atendimento e identifique o que precisa de atenção.',
  },
  {
    icon: 'users',
    title: 'Transbordo para a equipe',
    description: 'Assuma uma conversa quando a interação precisar de atendimento humano.',
  },
  {
    icon: 'settings',
    title: 'Configuração personalizada',
    description: 'Adapte informações, orientações e o jeito de conversar ao seu negócio.',
  },
];

const FAQS = [
  {
    question: 'O que é o AtendaBot?',
    answer: 'O AtendaBot é uma plataforma que automatiza respostas com inteligência artificial, organiza conversas e mantém a equipe no controle de cada interação.',
  },
  {
    question: 'Ele pode ser usado por diferentes tipos de negócio?',
    answer: 'Sim. A configuração pode considerar as informações, as rotinas e o tom de comunicação de cada operação.',
  },
  {
    question: 'A equipe continua no controle dos atendimentos?',
    answer: 'Sim. Quando uma conversa precisar de atenção humana, a equipe pode assumir o atendimento e seguir com o contexto disponível.',
  },
  {
    question: 'A inteligência artificial substitui a equipe?',
    answer: 'A proposta é automatizar tarefas recorrentes e organizar a operação. Decisões e situações sensíveis continuam sob responsabilidade das pessoas.',
  },
  {
    question: 'Como faço para acessar?',
    answer: 'Use o botão Entrar para acessar sua conta e continuar para o ambiente do AtendaBot.',
  },
];

const LandingIcon = ({ name }) => {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (name) {
    case 'spark':
      return <svg {...commonProps}><path d="m12 3 1.6 4.2L18 9l-4.4 1.8L12 15l-1.6-4.2L6 9l4.4-1.8L12 3Z" /><path d="m18.5 15 .8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" /></svg>;
    case 'inbox':
      return <svg {...commonProps}><path d="M4 5h16l2 9v5H2v-5l2-9Z" /><path d="M2 14h5l2 3h6l2-3h5" /></svg>;
    case 'history':
      return <svg {...commonProps}><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5M12 7v5l3 2" /></svg>;
    case 'chart':
      return <svg {...commonProps}><path d="M4 19V9M10 19V5M16 19v-7M22 19H2" /></svg>;
    case 'users':
      return <svg {...commonProps}><path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" /><circle cx="9" cy="7" r="3" /><path d="M17 11a3 3 0 1 0 0-6M22 20v-1.5a4 4 0 0 0-3-3.7" /></svg>;
    case 'settings':
      return <svg {...commonProps}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" /></svg>;
    case 'shield':
      return <svg {...commonProps}><path d="M12 22s8-3.7 8-10V5l-8-3-8 3v7c0 6.3 8 10 8 10Z" /><path d="m8.5 12 2.2 2.2 4.8-5" /></svg>;
    case 'check':
      return <svg {...commonProps}><path d="m5 12 4 4L19 6" /></svg>;
    case 'arrow':
      return <svg {...commonProps}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    default:
      return null;
  }
};

const AtendabotLanding = () => (
  <main className="atendabot-landing atendabot-landing--home">
    <header className="atendabot-landing__header">
      <div className="atendabot-landing__container atendabot-landing__header-inner">
        <a className="atendabot-landing__brand" href="#inicio" aria-label="AtendaBot — início">
          <span className="atendabot-landing__brand-mark" aria-hidden="true">
            <img src={ATENDABOT_LOGO} alt="" />
          </span>
          <span className="atendabot-landing__brand-name" aria-hidden="true">
            atenda<strong>bot</strong>
          </span>
        </a>

        <nav className="atendabot-landing__nav" aria-label="Navegação principal">
          <a href="#recursos">Recursos</a>
          <a href="#como-funciona">Como funciona</a>
          <a href="#beneficios">Benefícios</a>
          <a href="#perguntas">Perguntas</a>
        </nav>

        <Link className="atendabot-landing__login-link" to="/login">
          Entrar
        </Link>
      </div>
    </header>

    <section id="inicio" className="atendabot-landing__hero" aria-labelledby="atendabot-landing-title">
      <div className="atendabot-landing__container atendabot-landing__hero-grid">
        <div className="atendabot-landing__content">
          <p className="atendabot-landing__eyebrow">
            <span aria-hidden="true" />
            Automação inteligente com IA
          </p>

          <h1 id="atendabot-landing-title">
            Atendimento automatizado por IA, com sua equipe <span>no controle.</span>
          </h1>

          <p className="atendabot-landing__description">
            O AtendaBot usa inteligência artificial para responder automaticamente, manter o contexto das conversas e organizar o atendimento em um só lugar.
          </p>

          <div className="atendabot-landing__hero-actions">
            <Link className="atendabot-landing__primary-cta" to="/login">
              Acessar minha conta
              <LandingIcon name="arrow" />
            </Link>
            <a className="atendabot-landing__secondary-cta" href="#recursos">
              Conhecer os recursos
            </a>
          </div>

          <p className="atendabot-landing__hero-note">
            <LandingIcon name="check" />
            A IA cuida das demandas recorrentes e sua equipe assume quando precisar.
          </p>
        </div>

        <div
          className="atendabot-landing__product-preview"
          role="img"
          aria-label="Prévia ilustrativa de uma conversa organizada no painel do AtendaBot"
        >
          <div className="atendabot-landing__preview-window">
            <div className="atendabot-landing__preview-topbar">
              <span className="atendabot-landing__preview-dots" aria-hidden="true"><i /><i /><i /></span>
              <span>Painel de atendimento</span>
              <span className="atendabot-landing__online"><i /> Online</span>
            </div>

            <div className="atendabot-landing__preview-body">
              <div className="atendabot-landing__preview-sidebar" aria-hidden="true">
                <span className="is-active" />
                <span />
                <span />
                <span />
              </div>

              <div className="atendabot-landing__conversation">
                <div className="atendabot-landing__conversation-head">
                  <span className="atendabot-landing__avatar">M</span>
                  <span><strong>Nova conversa</strong><small>Atendimento em andamento</small></span>
                  <span className="atendabot-landing__ai-badge">IA ativa</span>
                </div>
                <div className="atendabot-landing__messages">
                  <p className="atendabot-landing__message atendabot-landing__message--customer">
                    Olá! Gostaria de saber mais sobre o serviço.
                  </p>
                  <p className="atendabot-landing__message atendabot-landing__message--bot">
                    Olá! Posso explicar como funciona e ajudar com suas dúvidas.
                  </p>
                  <p className="atendabot-landing__message atendabot-landing__message--customer atendabot-landing__message--short">
                    Ótimo, vamos começar.
                  </p>
                </div>
                <div className="atendabot-landing__composer">
                  <span>Escreva uma mensagem...</span>
                  <i aria-hidden="true">→</i>
                </div>
              </div>
            </div>
          </div>

          <div className="atendabot-landing__preview-card atendabot-landing__preview-card--context">
            <span>Contexto</span>
            <strong>Informações organizadas</strong>
          </div>
          <div className="atendabot-landing__preview-card atendabot-landing__preview-card--team">
            <span className="atendabot-landing__preview-check"><LandingIcon name="check" /></span>
            <span><strong>Equipe no controle</strong><small>Pronta para assumir</small></span>
          </div>
        </div>
      </div>
    </section>

    <section className="atendabot-landing__highlights" aria-label="Diferenciais do AtendaBot">
      <div className="atendabot-landing__container">
        <article><span>01</span><div><strong>Respostas automáticas</strong><p>Agilidade e consistência nas demandas recorrentes.</p></div></article>
        <article><span>02</span><div><strong>Contexto centralizado</strong><p>Informações acessíveis para dar continuidade.</p></div></article>
        <article><span>03</span><div><strong>Controle compartilhado</strong><p>IA e equipe trabalhando na mesma operação.</p></div></article>
      </div>
    </section>

    <section className="atendabot-landing__section atendabot-landing__value-section" aria-labelledby="atendabot-value-title">
      <div className="atendabot-landing__container atendabot-landing__value-grid">
        <div className="atendabot-landing__section-heading">
          <p className="atendabot-landing__section-kicker">Por que centralizar</p>
          <h2 id="atendabot-value-title">Cada conversa precisa de contexto, continuidade e atenção.</h2>
          <p>
            Quando mensagens, informações e responsabilidades ficam dispersas, atender bem se torna mais difícil. O AtendaBot ajuda a transformar esse fluxo em uma rotina mais clara.
          </p>
        </div>

        <div className="atendabot-landing__value-list">
          <article>
            <span>Mensagens dispersas</span>
            <strong>Uma visão organizada do atendimento</strong>
            <p>Acompanhe o que está acontecendo e encontre conversas com mais facilidade.</p>
          </article>
          <article>
            <span>Respostas inconsistentes</span>
            <strong>Orientações alinhadas ao negócio</strong>
            <p>Use uma base configurada para apoiar respostas mais coerentes.</p>
          </article>
          <article>
            <span>Pouca visibilidade</span>
            <strong>Informações que ajudam a priorizar</strong>
            <p>Observe a rotina pelo painel e identifique atendimentos que pedem atenção.</p>
          </article>
        </div>
      </div>
    </section>

    <section id="recursos" className="atendabot-landing__section atendabot-landing__features-section" aria-labelledby="atendabot-features-title">
      <div className="atendabot-landing__container">
        <div className="atendabot-landing__section-heading atendabot-landing__section-heading--center">
          <p className="atendabot-landing__section-kicker">Recursos essenciais</p>
          <h2 id="atendabot-features-title">Recursos para sua equipe conversar melhor.</h2>
          <p>Uma base simples para organizar a operação e dar fluidez ao atendimento.</p>
        </div>

        <div className="atendabot-landing__features-grid">
          {FEATURES.map((feature) => (
            <article className="atendabot-landing__feature-card" key={feature.title}>
              <span className="atendabot-landing__feature-icon"><LandingIcon name={feature.icon} /></span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="como-funciona" className="atendabot-landing__section atendabot-landing__steps-section" aria-labelledby="atendabot-steps-title">
      <div className="atendabot-landing__container">
        <div className="atendabot-landing__section-heading atendabot-landing__section-heading--center atendabot-landing__section-heading--light">
          <p className="atendabot-landing__section-kicker">Como funciona</p>
          <h2 id="atendabot-steps-title">Da configuração ao acompanhamento, sem complicação.</h2>
        </div>

        <ol className="atendabot-landing__steps">
          <li>
            <span>01</span>
            <div><h3>Organize as informações</h3><p>Reúna o que a IA e a equipe precisam saber para orientar as conversas.</p></div>
          </li>
          <li>
            <span>02</span>
            <div><h3>Personalize o atendimento</h3><p>Ajuste instruções e linguagem para refletir a forma de comunicar do seu negócio.</p></div>
          </li>
          <li>
            <span>03</span>
            <div><h3>Acompanhe e assuma quando precisar</h3><p>Observe a operação pelo painel e mantenha a equipe próxima das interações.</p></div>
          </li>
        </ol>
      </div>
    </section>

    <section id="beneficios" className="atendabot-landing__section atendabot-landing__benefits-section" aria-labelledby="atendabot-benefits-title">
      <div className="atendabot-landing__container atendabot-landing__benefits-grid">
        <div className="atendabot-landing__control-card">
          <p className="atendabot-landing__section-kicker">Controle para sua equipe</p>
          <h2 id="atendabot-benefits-title">Tecnologia para apoiar pessoas, não afastá-las.</h2>
          <p>Automatize o que se repete e preserve a atenção humana para conversas que exigem análise, cuidado ou decisão.</p>
          <ul>
            <li><LandingIcon name="check" /> Continuidade com histórico e contexto</li>
            <li><LandingIcon name="check" /> Visibilidade sobre a rotina de atendimento</li>
            <li><LandingIcon name="check" /> Liberdade para a equipe assumir conversas</li>
          </ul>
        </div>

        <div className="atendabot-landing__security-card">
          <span className="atendabot-landing__security-icon"><LandingIcon name="shield" /></span>
          <p className="atendabot-landing__section-kicker">Uso responsável das informações</p>
          <h3>Privacidade também faz parte de um bom atendimento.</h3>
          <p>
            Use o AtendaBot em conjunto com as políticas e os processos do seu negócio, mantendo atenção aos princípios da LGPD e ao tratamento responsável dos dados.
          </p>
          <span className="atendabot-landing__security-note">Clareza, controle e responsabilidade na operação.</span>
        </div>
      </div>
    </section>

    <section id="perguntas" className="atendabot-landing__section atendabot-landing__faq-section" aria-labelledby="atendabot-faq-title">
      <div className="atendabot-landing__container atendabot-landing__faq-grid">
        <div className="atendabot-landing__section-heading">
          <p className="atendabot-landing__section-kicker">Perguntas frequentes</p>
          <h2 id="atendabot-faq-title">Informação clara para começar com confiança.</h2>
          <p>Conheça os pontos principais sobre o AtendaBot e o papel da sua equipe no atendimento.</p>
        </div>

        <div className="atendabot-landing__faq-list">
          {FAQS.map((item) => (
            <details key={item.question}>
              <summary><span>{item.question}</span><i aria-hidden="true" /></summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <section className="atendabot-landing__final-cta" aria-labelledby="atendabot-final-title">
      <div className="atendabot-landing__container">
        <div className="atendabot-landing__final-card">
          <div>
            <p className="atendabot-landing__section-kicker">Seu atendimento, mais organizado</p>
            <h2 id="atendabot-final-title">Pronto para continuar no AtendaBot?</h2>
            <p>Acesse sua conta e acompanhe suas conversas em um só lugar.</p>
          </div>
          <Link className="atendabot-landing__primary-cta atendabot-landing__primary-cta--light" to="/login">
            Entrar no AtendaBot
            <LandingIcon name="arrow" />
          </Link>
        </div>
      </div>
    </section>

    <footer className="atendabot-landing__footer">
      <div className="atendabot-landing__container">
        <a className="atendabot-landing__brand atendabot-landing__brand--footer" href="#inicio" aria-label="AtendaBot — voltar ao início">
          <span className="atendabot-landing__brand-mark" aria-hidden="true">
            <img src={ATENDABOT_LOGO} alt="" />
          </span>
          <span className="atendabot-landing__brand-name" aria-hidden="true">
            atenda<strong>bot</strong>
          </span>
        </a>
        <nav className="atendabot-landing__footer-links" aria-label="Navegação do rodapé">
          <a href="#recursos">Recursos</a>
          <a href="#como-funciona">Como funciona</a>
          <a href="#beneficios">Benefícios</a>
          <a href="#perguntas">Perguntas</a>
        </nav>
        <span>© {new Date().getFullYear()} AtendaBot</span>
      </div>
    </footer>
  </main>
);

export const AtendabotAccessUnavailable = () => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logoutUser();
    navigate('/', { replace: true });
  };

  return (
    <main className="atendabot-landing atendabot-access-unavailable">
      <div className="atendabot-landing__glow atendabot-landing__glow--top" aria-hidden="true" />
      <div className="atendabot-landing__glow atendabot-landing__glow--bottom" aria-hidden="true" />

      <section className="atendabot-access-unavailable__card" aria-labelledby="access-unavailable-title">
        <img src={ATENDABOT_LOGO} alt="Atendabot" />
        <p className="atendabot-access-unavailable__eyebrow">Acesso indisponível</p>
        <h1 id="access-unavailable-title">Esta conta não possui acesso ao AtendaBot.</h1>
        <p className="atendabot-access-unavailable__description">
          Saia e entre com uma conta que tenha o AtendaBot habilitado para continuar.
        </p>
        <button type="button" className="atendabot-access-unavailable__logout" onClick={handleLogout}>
          Sair
        </button>
      </section>
    </main>
  );
};

export default AtendabotLanding;
